import { useEffect, useMemo, useState } from 'react';
import TopBar from '../components/TopBar';
import { api } from '../api/client';

type Tramite = {
  id: number;
  categoria: string;
  titulo: string;
  donde: string | null;
  horario: string | null;
  costo: string | null;
  link_online: string | null;
};

type TramiteDetalle = Tramite & {
  descripcion: string | null;
  requisitos: string[];
};

export default function Tramites() {
  const [items, setItems]         = useState<Tramite[]>([]);
  const [cargando, setCargando]   = useState(true);
  const [err, setErr]             = useState<string | null>(null);
  const [q, setQ]                 = useState('');
  const [expandido, setExpandido] = useState<number | null>(null);
  const [detalles, setDetalles]   = useState<Record<number, TramiteDetalle>>({});
  const [cargDet, setCargDet]     = useState<number | null>(null);

  useEffect(() => {
    api<{ items: Tramite[] }>('/tramites')
      .then((d) => setItems(d.items))
      .catch((e) => setErr(e.message))
      .finally(() => setCargando(false));
  }, []);

  async function toggle(id: number) {
    if (expandido === id) { setExpandido(null); return; }
    setExpandido(id);
    if (!detalles[id]) {
      setCargDet(id);
      try {
        const d = await api<TramiteDetalle>(`/tramites?id=${id}`);
        setDetalles((prev) => ({ ...prev, [id]: d }));
      } catch {}
      finally { setCargDet(null); }
    }
  }

  const filtrados = useMemo(() => {
    if (!q.trim()) return items;
    const lower = q.toLowerCase();
    return items.filter(
      (t) => t.titulo.toLowerCase().includes(lower) || t.categoria.toLowerCase().includes(lower)
    );
  }, [items, q]);

  const porCategoria = useMemo(() => {
    const mapa: Record<string, Tramite[]> = {};
    for (const t of filtrados) {
      if (!mapa[t.categoria]) mapa[t.categoria] = [];
      mapa[t.categoria].push(t);
    }
    return mapa;
  }, [filtrados]);

  return (
    <>
      <TopBar title="Guía de trámites" back />
      <main className="max-w-md mx-auto px-4 py-4 pb-24 space-y-4">

        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-base">🔍</span>
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar trámite…"
            className="w-full rounded-xl pl-9 pr-4 py-3 text-sm outline-none"
            style={{ backgroundColor: '#ffffff', border: '1px solid #6e6b6b', color: '#303030' }}
          />
        </div>

        {cargando && <p className="text-center text-gray-400 pt-6">Cargando…</p>}
        {err && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl p-3">{err}</div>
        )}
        {!cargando && filtrados.length === 0 && (
          <div className="text-center py-10">
            <p className="text-3xl mb-3">📄</p>
            <p className="text-gray-500 text-sm">
              {q.trim() ? `Sin resultados para "${q}"` : 'No hay trámites disponibles.'}
            </p>
          </div>
        )}

        {Object.entries(porCategoria).map(([cat, tramites]) => (
          <div key={cat}>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">{cat}</p>
            <div className="space-y-2">
              {tramites.map((t) => {
                const abierto = expandido === t.id;
                const det = detalles[t.id];
                return (
                  <div
                    key={t.id}
                    className="bg-white border border-app rounded-xl overflow-hidden shadow-sm"
                  >
                    <button
                      onClick={() => toggle(t.id)}
                      className="w-full text-left px-4 py-3.5 flex items-center justify-between gap-3 active:bg-gray-50"
                    >
                      <p className="font-semibold text-sm text-gray-900 flex-1 leading-snug">{t.titulo}</p>
                      <span className="text-gray-400 text-sm flex-shrink-0">{abierto ? '▲' : '▼'}</span>
                    </button>

                    {abierto && (
                      <div className="border-t border-gray-100 px-4 pb-4 pt-3 space-y-3">
                        {cargDet === t.id && (
                          <p className="text-xs text-gray-400">Cargando…</p>
                        )}
                        {det && (
                          <>
                            {det.descripcion && (
                              <p className="text-sm text-gray-600 leading-relaxed">{det.descripcion}</p>
                            )}

                            {det.requisitos.length > 0 && (
                              <div>
                                <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-2">
                                  Requisitos
                                </p>
                                <ul className="space-y-1.5">
                                  {det.requisitos.map((r, i) => (
                                    <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                                      <span className="font-bold flex-shrink-0 mt-0.5" style={{ color: '#32329f' }}>·</span>
                                      {r}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            <div className="flex flex-wrap gap-3 pt-1">
                              {det.donde && <InfoChip icon="📍" label={det.donde} />}
                              {det.horario && <InfoChip icon="🕐" label={det.horario} />}
                              {det.costo && <InfoChip icon="💰" label={det.costo} />}
                            </div>

                            {det.link_online && (
                              <a
                                href={det.link_online}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block w-full text-center text-white rounded-xl py-2.5 font-semibold text-sm"
                                style={{ backgroundColor: '#32329f' }}
                              >
                                Hacer en línea →
                              </a>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </main>
    </>
  );
}

function InfoChip({ icon, label }: { icon: string; label: string }) {
  return (
    <div className="flex items-start gap-1.5 text-xs text-gray-600 bg-gray-50 rounded-lg px-2.5 py-1.5">
      <span className="flex-shrink-0">{icon}</span>
      <span className="leading-snug">{label}</span>
    </div>
  );
}
